// src/screens/team/MyTeamsRedirect.tsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "../supabaseClient";

export default function MyTeamsRedirect() {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadAssignedTeam() {
      if (!clubId) {
        setErrorMsg("Club introuvable.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMsg(null);

        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) throw new Error("Utilisateur non connecté.");

        /**
         * À ADAPTER à ta future structure réelle.
         * Ici on suppose une table `team_members` avec :
         * - team_id
         * - club_id
         * - club_member_id
         *
         * et qu'on retrouve d'abord le club_member courant.
         */

        const { data: clubMember, error: clubMemberError } = await supabase
          .from("club_members")
          .select("id")
          .eq("club_id", clubId)
          .eq("user_id", user.id)
          .eq("status", "active")
          .maybeSingle();

        if (clubMemberError) throw clubMemberError;
        if (!clubMember?.id) {
          throw new Error("Aucun profil membre actif trouvé dans ce club.");
        }

        const { data: teamMemberRows, error: teamMemberError } = await supabase
          .from("team_members")
          .select("team_id")
          .eq("club_id", clubId)
          .eq("club_member_id", clubMember.id)
          .limit(1);

        if (teamMemberError) throw teamMemberError;

        const teamId = teamMemberRows?.[0]?.team_id;

        if (!teamId) {
          throw new Error("Aucune équipe assignée pour le moment.");
        }

        if (!cancelled) {
          navigate(`/club/${clubId}/team/${teamId}`, { replace: true });
        }
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message || "Impossible de charger l’équipe.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAssignedTeam();

    return () => {
      cancelled = true;
    };
  }, [clubId, navigate]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Chargement de ton équipe…
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 mt-0.5" />
          <div>
            <div className="font-semibold">Mon équipe indisponible</div>
            <div className="text-sm mt-1">{errorMsg}</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}